// app/pages/AddBalancePage.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  X,
  CreditCard,
  DollarSign,
  CheckCircle,
  Building,
  Coins,
  AlertTriangle,
  Loader2,
  Info,
  AlertCircle,
  User,
  ChevronLeft,
} from "lucide-react";
// import { loadStripe } from "@stripe/stripe-js";   // Stripe — disabled, not in use
// import { Elements } from "@stripe/react-stripe-js"; // Stripe — disabled, not in use
import creditAccountsService from "../../services/creditAccountsService"
import paymentMethodsService from "../../services/paymentMethodsService";
import toast from "react-hot-toast";
import { useCurrentUser } from "../../utils/context/UserProvider";

// import StripeCardForm from "./PymentMethods/StripeCardForm"; // Stripe — disabled, not in use
// import PayPalPayment from "./PymentMethods/PayPalPayment";   // PayPal — disabled, not in use
import BankTransferPayment from "./PymentMethods/BankTransferPayment";
import PayoneerPayment from "./PymentMethods/PayoneerPayment";
import USDTPayment from "./PymentMethods/USDTPayment";
import PaymentSummary from "./PymentMethods/PaymentSummary";
import { PaymentMethodCard } from "../../components";

// const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || ""); // Stripe — disabled

// Layout constants
const DEFAULT_TOPBAR = 64;

// Defaults if crypto not configured
const DEFAULT_COINS = [
  { coin: "USDT / USDC", enabled: false, network: "ERC20", wallet: "", additionalInfo: "" },
  { coin: "BTC", enabled: false, network: "ERC20", wallet: "", additionalInfo: "" },
];

function normalizeCryptoBlock(cryptoMaybe, legacyUsdtMaybe) {
  if (cryptoMaybe && Array.isArray(cryptoMaybe.coins)) {
    const map = new Map(cryptoMaybe.coins.map((c) => [c.coin, c]));
    DEFAULT_COINS.forEach((def) => {
      if (!map.has(def.coin)) map.set(def.coin, { ...def });
      else {
        const c = map.get(def.coin);
        map.set(def.coin, {
          coin: def.coin,
          enabled: !!c.enabled,
          network: c.network || def.network,
          wallet: c.wallet || "",
          additionalInfo: c.additionalInfo || "",
        });
      }
    });
    return { enabled: !!cryptoMaybe.enabled, coins: Array.from(map.values()) };
  }
  if (legacyUsdtMaybe) {
    return {
      enabled: !!legacyUsdtMaybe.enabled,
      coins: [
        {
          coin: "USDT",
          enabled: !!legacyUsdtMaybe.enabled,
          network: legacyUsdtMaybe.network || "TRC20",
          wallet: legacyUsdtMaybe.address || "",
          additionalInfo: legacyUsdtMaybe.additionalInfo || "",
        },
        ...DEFAULT_COINS.filter((c) => c.coin !== "USDT"),
      ],
    };
  }
  return { enabled: false, coins: DEFAULT_COINS.map((c) => ({ ...c })) };
}

export default function AddBalancePage() {
  const navigate = useNavigate();
  const { currentUser } = useCurrentUser();
  const { search } = useLocation();
  const qs = useMemo(() => new URLSearchParams(search), [search]);

  // Selected account (from query)
  const initialSelected = {
    id: qs.get("accountId") || "",
    name: qs.get("name") || "",
    balance: qs.get("balance") || "0.00",
    currency: qs.get("currency") || "USD",
    clientName: qs.get("clientName") || "",
  };

  // Steps
  const [currentStep, setCurrentStep] = useState(0);
  const steps = [{ title: "Amount" }, { title: "Method" }, { title: "Details" }];

  // Amount as string
  const [amount, setAmount] = useState("");

  // Derived numeric value (safe)
  const amountValue = parseFloat(String(amount).replace(",", ".")) || 0;
  const total = amountValue;

  // Account list/state
  const [selectedAccount, setSelectedAccount] = useState(initialSelected);
  const [accounts, setAccounts] = useState([]);

  // Payment method + per-method fields
  const [paymentMethod, setPaymentMethod] = useState("");
  const [receiptFile, setReceiptFile] = useState(null);

  const [paypalEmail, setPaypalEmail] = useState("");
  const [usdtAddress, setUsdtAddress] = useState("");
  const [usdtNetwork, setUsdtNetwork] = useState("TRC20");
  const [usdtTxId, setUsdtTxId] = useState("");
  const [payoneerEmail, setPayoneerEmail] = useState("");
  const [payoneerTransactionId, setPayoneerTransactionId] = useState("");
  const [bankTransferReference, setBankTransferReference] = useState("");

  // UI state
  const [isProcessing, setIsProcessing] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [error, setError] = useState(null);
  const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(false);

  const [paymentMethods, setPaymentMethods] = useState({
    bankTransfer: {
      enabled: false,
      bankName: "",
      accountName: "",
      accountNumber: "",
      routingNumber: "",
      swiftCode: "",
      address: "",
      additionalInfo: "",
    },
    crypto: { enabled: false, coins: DEFAULT_COINS.map((c) => ({ ...c })) },
    payoneer: { enabled: false, email: "", accountName: "" },
    paypal: { enabled: false, email: "", businessName: "" },
    creditCard: { enabled: false },
  });

  // Layout measurement
  const contentRef = useRef(null);
  const headerRef = useRef(null);
  const [bounds, setBounds] = useState({ left: 0, width: 0 });
  const [headerH, setHeaderH] = useState(72);

  useEffect(() => {
    const measure = () => {
      const rect = contentRef.current?.getBoundingClientRect();
      if (rect) setBounds({ left: rect.left + window.scrollX, width: rect.width });
      if (headerRef.current) setHeaderH(headerRef.current.offsetHeight || 64);
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (contentRef.current) ro.observe(contentRef.current);
    if (headerRef.current) ro.observe(headerRef.current);
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, { passive: true });
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure);
    };
  }, []);

  // Mount: load accounts + payment methods
  useEffect(() => {
    (async () => {
      await fetchCreditAccounts();
      await fetchPaymentMethods();
      setBankTransferReference(currentUser?.clientId || "");
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPaymentMethods = async () => {
    try {
      setLoadingPaymentMethods(true);
      const res = await paymentMethodsService.get();
      const data = res?.data?.data || {};
      const normalizedCrypto = normalizeCryptoBlock(data.crypto, data.usdt);
      setPaymentMethods({
        bankTransfer: {
          enabled: data.bankTransfer?.enabled || false,
          bankName: data.bankTransfer?.bankName || "",
          accountName: data.bankTransfer?.accountName || "",
          accountNumber: data.bankTransfer?.accountNumber || "",
          routingNumber: data.bankTransfer?.routingNumber || "",
          swiftCode: data.bankTransfer?.swiftCode || "",
          address: data.bankTransfer?.address || "",
          additionalInfo: data.bankTransfer?.additionalInfo || "",
        },
        crypto: normalizedCrypto,
        payoneer: {
          enabled: data.payoneer?.enabled || false,
          email: data.payoneer?.email || "",
          accountName: data.payoneer?.accountName || "",
        },
        paypal: {
          enabled: data.paypal?.enabled || false,
          email: data.paypal?.email || "",
          businessName: data.paypal?.accountName || "",
        },
        creditCard: { enabled: data.creditCard?.enabled || false },
      });
    } catch (e) {
      console.error("Failed to fetch payment methods:", e);
      toast.error("Failed to load payment methods");
    } finally {
      setLoadingPaymentMethods(false);
    }
  };

  const fetchCreditAccounts = async () => {
    try {
      if (currentUser?.role?.name === "Client") {
        const r = await creditAccountsService.getUserAccount({});
        const d = r?.data;
        if (d?.statusCode === 200) {
          const a = d.data;
          const acc = {
            id: a._id,
            name: a.account_name,
            balance: a.balance,
            currency: a.account_currency,
            clientName: a.user?.reference || a.user?.fullName || "",
          };
          setAccounts([acc]);
          if (!initialSelected.id) setSelectedAccount(acc);
        }
      } else {
        const r = await creditAccountsService.searchAccounts();
        const d = r?.data;
        if (d?.statusCode === 200) {
          const list = d.data.map((item) => ({
            id: item._id,
            name: item.account_name,
            balance: item.balance,
            currency: item.account_currency,
            clientName: item.user?.reference || item.user?.fullName || "",
          }));
          setAccounts(list);
          if (!initialSelected.id && list.length) setSelectedAccount(list[0]);
        }
      }
    } catch (e) {
      console.error("Failed to fetch accounts:", e);
      toast.error("Failed to load account. Please try again.");
    }
  };

  const formatCurrency = (value, currencyCode = selectedAccount.currency) =>
    currencyCode === "USDT / USDC" || currencyCode === "BTC"
      ? `${Number(value || 0).toFixed(2)} ${currencyCode}`
      : new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: currencyCode,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);

  const resetState = () => {
    setCurrentStep(0);
    setAmount("");
    setPaymentMethod("");
    setReceiptFile(null);
    setUsdtAddress("");
    setUsdtNetwork("TRC20");
    setUsdtTxId("");
    setPayoneerEmail("");
    setPayoneerTransactionId("");
    setPaypalEmail("");
    setIsProcessing(false);
    setIsComplete(false);
    setError(null);
  };

  const stepsValid = () => {
    switch (currentStep) {
      case 0:
        return amountValue >= 100;
      case 1:
        return getAvailablePaymentMethods().length > 0 && paymentMethod !== "";
      case 2:
        // if (paymentMethod === "creditCard") return true; // Stripe — disabled
        if (paymentMethod === "bankTransfer") return receiptFile !== null;
        if (paymentMethod === "payoneer")
          return (
            !!payoneerEmail &&
            /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(payoneerEmail) &&
            !!payoneerTransactionId
          );
        if (paymentMethod === "crypto") return !!usdtAddress && !!usdtTxId;
        // if (paymentMethod === "paypal") // PayPal — disabled
        //   return !!paypalEmail && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(paypalEmail);
        return true;
      default:
        return false;
    }
  };

  const getAvailablePaymentMethods = () => {
    const methods = [];
    // PayPal — disabled, not in use
    // if (paymentMethods.paypal.enabled)
    //   methods.push({
    //     id: "paypal",
    //     title: "PayPal",
    //     icon: <CreditCard size={16} />,
    //     description: "Pay with PayPal or credit/debit card",
    //   });
    if (paymentMethods.bankTransfer.enabled)
      methods.push({
        id: "bankTransfer",
        title: "Bank Transfer",
        icon: <Building size={16} />,
        description: "Make a bank transfer and upload your receipt",
      });
    if (paymentMethods.payoneer.enabled)
      methods.push({
        id: "payoneer",
        title: "Payoneer",
        icon: <CreditCard size={16} />,
        description: "Pay using your Payoneer account",
      });
    const anyCoinEnabled = (paymentMethods.crypto?.coins || []).some(
      (c) => c.enabled && c.wallet
    );
    if (paymentMethods.crypto.enabled && anyCoinEnabled)
      methods.push({
        id: "crypto",
        title: "Crypto",
        icon: <Coins size={16} />,
        description: "Pay with USDT / USDC / BTC",
      });
    // Credit Card (Stripe) — disabled, not in use
    // if (paymentMethods.creditCard.enabled)
    //   methods.push({
    //     id: "creditCard",
    //     title: "Credit Card",
    //     icon: <CreditCard size={16} />,
    //     description: "Pay instantly with your credit or debit card",
    //   });
    return methods;
  };

  const handleNext = () => {
    if (currentStep === 0 && getAvailablePaymentMethods().length === 0) {
      setError("No payment methods are available. Please contact support for assistance.");
      return;
    }
    if (currentStep < steps.length - 1) setCurrentStep((s) => s + 1);
  };

  const handlePrev = () => setCurrentStep((s) => Math.max(0, s - 1));

  // small UI primitives
  const Card = ({ children, className = "" }) => (
    <div
      className={`rounded-xl border border-neutral-200 bg-white p-5 shadow-sm text-left dark:bg-neutral-950 dark:border-neutral-900 ${className}`}
    >
      {children}
    </div>
  );

  const AccountCard = () => (
    <Card>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-neutral-100 dark:bg-neutral-900/30 flex items-center justify-center">
            <User size={18} className="text-neutral-600 dark:text-neutral-300" />
          </div>
          <div className="text-left">
            <div className="text-sm font-medium text-neutral-900 dark:text-white">
              {selectedAccount.name}
            </div>
            <div className="text-xs text-neutral-500 dark:text-neutral-400">
              {currentUser?.role?.name === "Client"
                ? "Your account"
                : `${selectedAccount.clientName} Account`}
            </div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-xs text-neutral-500 dark:text-neutral-400">Current Balance</div>
          <div className="text-sm font-semibold text-neutral-900 dark:text-white">
            {formatCurrency(parseFloat(selectedAccount.balance))}
          </div>
        </div>
      </div>
    </Card>
  );

  const FieldCard = ({ children, title, hint }) => (
    <Card>
      {title && (
        <div className="mb-2 text-xs font-medium text-neutral-700 dark:text-neutral-200">
          {title}
        </div>
      )}
      {children}
      {hint && (
        <div className="mt-3 flex items-start gap-2 rounded-xl border border-yellow-200 bg-yellow-50 p-3 dark:bg-yellow-900/20 dark:border-yellow-800">
          <AlertCircle size={14} className="text-yellow-600 dark:text-yellow-400 mt-0.5" />
          <div className="text-left">
            <div className="text-xs font-medium text-yellow-800 dark:text-yellow-200">
              Important
            </div>
            <div className="text-xs text-yellow-700 dark:text-yellow-300">{hint}</div>
          </div>
        </div>
      )}
    </Card>
  );

  const StepAmount = (
    <div className="space-y-5">
      <AccountCard />
      <div className="rounded-xl border border-neutral-200 bg-white p-5 shadow-sm text-left dark:bg-neutral-950 dark:border-neutral-900">
        <div className="mb-2 text-xs font-medium text-neutral-700 dark:text-neutral-200">
          Enter Amount ({selectedAccount.currency})
        </div>
        <div className="relative">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <DollarSign size={16} className="text-neutral-400 dark:text-neutral-500" />
          </div>

          <input
            type="number"
            className="w-full pl-9 pr-3 py-2.5 border border-neutral-300 dark:border-neutral-800 rounded-xl bg-white dark:bg-neutral-900/30 text-neutral-800 dark:text-neutral-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
            placeholder="0.00"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            min="0"
            step="0.01"
          />
        </div>

        {amountValue > 0 && (
          <div className="mt-3 flex items-center justify-between rounded-xl bg-neutral-50 dark:bg-neutral-900/50 px-3 py-2">
            <span className="text-xs font-medium text-neutral-600 dark:text-neutral-300">
              Total Amount
            </span>
            <span className="text-sm font-semibold text-neutral-900 dark:text-white">
              {formatCurrency(total)}
            </span>
          </div>
        )}

        {amountValue > 0 && amountValue < 100 && (
          <div className="mt-2 flex items-center gap-1.5 px-1">
            <AlertCircle size={13} className="text-red-500 dark:text-red-400 shrink-0" />
            <span className="text-xs text-red-500 dark:text-red-400">
              Minimum deposit is $100
            </span>
          </div>
        )}
        <div className="mt-3 flex items-start gap-2 rounded-xl border border-yellow-200 bg-yellow-50 p-3 dark:bg-yellow-900/20 dark:border-yellow-800">
          <AlertCircle size={14} className="text-yellow-600 dark:text-yellow-400 mt-0.5" />
          <div className="text-left">
            <div className="text-xs font-medium text-yellow-800 dark:text-yellow-200">
              Important
            </div>
            <div className="text-xs text-yellow-700 dark:text-yellow-300">
              Please include your fees with the payment.
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const t = (x) => x; // passthrough

  const StepMethod = (
    <div className="space-y-5">
      <FieldCard title="Select Payment Method">
        {loadingPaymentMethods ? (
          <div className="flex items-center justify-center p-10">
            <Loader2 size={22} className="animate-spin text-neutral-500 dark:text-neutral-400" />
            <span className="ml-2 text-sm text-neutral-600 dark:text-neutral-300">
              Loading payment methods…
            </span>
          </div>
        ) : getAvailablePaymentMethods().length > 0 ? (
          <div className="grid gap-3 sm:grid-cols-2">
            {getAvailablePaymentMethods().map((m) => (
              <PaymentMethodCard
                key={m.id}
                id={m.id}
                title={m.title}
                icon={m.icon}
                description={m.description}
                selected={paymentMethod === m.id}
                onClick={setPaymentMethod}
              />
            ))}
          </div>
        ) : (
          <div className="rounded-xl border border-neutral-200 dark:border-neutral-900 bg-neutral-50 dark:bg-neutral-900/50 p-6 text-left">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-neutral-200 dark:bg-neutral-700">
              <AlertTriangle size={20} className="text-neutral-700 dark:text-neutral-200" />
            </div>
            <div className="text-sm font-medium text-neutral-800 dark:text-neutral-100">
              No payment methods available
            </div>
            <div className="mt-1 text-xs text-neutral-600 dark:text-neutral-300">
              Please contact support for assistance.
            </div>
          </div>
        )}
      </FieldCard>

      <PaymentSummary
        selectedAccount={selectedAccount}
        amountValue={amountValue}
        paymentMethod={paymentMethod}
        total={total}
        formatCurrency={formatCurrency}
        t={t}
      />
    </div>
  );

  const StepDetails = (
    <div className="space-y-5">
      {/* PayPal — disabled, not in use */}
      {/* {paymentMethod === "paypal" && (
        <PayPalPayment
          paymentMethods={paymentMethods}
          paypalEmail={paypalEmail}
          setPaypalEmail={setPaypalEmail}
          total={total}
          amountValue={amountValue}
          accountId={selectedAccount.id}
          currency={selectedAccount.currency}
          onSuccess={() => setIsComplete(true)}
          onClose={() => navigate("/credit-accounts")}
          t={t}
        />
      )} */}

      {paymentMethod !== "creditCard" && (
        <>
          {paymentMethod === "bankTransfer" && (
            <BankTransferPayment
              paymentMethods={paymentMethods}
              formatCurrency={formatCurrency}
              total={total}
              bankTransferReference={bankTransferReference}
              receiptFile={receiptFile}
              setReceiptFile={setReceiptFile}
              accountId={selectedAccount.id}
              amountValue={amountValue}
              currency={selectedAccount.currency}
              onSuccess={() => setIsComplete(true)}
              onClose={() => navigate("/credit-accounts")}
              t={t}
            />
          )}

          {paymentMethod === "payoneer" && (
            <PayoneerPayment
              paymentMethods={paymentMethods}
              formatCurrency={formatCurrency}
              total={total}
              payoneerEmail={payoneerEmail}
              setPayoneerEmail={setPayoneerEmail}
              payoneerTransactionId={payoneerTransactionId}
              setPayoneerTransactionId={setPayoneerTransactionId}
              accountId={selectedAccount.id}
              amountValue={amountValue}
              currency={selectedAccount.currency}
              onSuccess={() => setIsComplete(true)}
              onClose={() => navigate("/credit-accounts")}
              t={t}
            />
          )}

          {paymentMethod === "crypto" && (
            <USDTPayment
              paymentMethods={paymentMethods}
              formatCurrency={formatCurrency}
              total={total}
              usdtAddress={usdtAddress}
              setUsdtAddress={setUsdtAddress}
              usdtNetwork={usdtNetwork}
              setUsdtNetwork={setUsdtNetwork}
              usdtTxId={usdtTxId}
              setUsdtTxId={setUsdtTxId}
              accountId={selectedAccount.id}
              amountValue={amountValue}
              currency={selectedAccount.currency}
              onSuccess={() => setIsComplete(true)}
              onClose={() => navigate("/credit-accounts")}
              t={t}
            />
          )}

          {!isComplete && (
            <PaymentSummary
              selectedAccount={selectedAccount}
              amountValue={amountValue}
              paymentMethod={paymentMethod}
              total={total}
              formatCurrency={formatCurrency}
              t={t}
            />
          )}
        </>
      )}
    </div>
  );

  const renderProcessingState = () => (
    <div className="py-10 text-left">
      <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-2 border-neutral-900 border-t-transparent dark:border-neutral-200 dark:border-t-transparent" />
      </div>
      <div className="mb-1 text-sm font-medium text-neutral-900 dark:text-white">
        Processing Payment
      </div>
      <div className="mb-4 text-xs text-neutral-600 dark:text-neutral-300">
        Please wait while we process your payment of {formatCurrency(total)}…
      </div>
      <div className="mx-auto h-1.5 w-full max-w-xs overflow-hidden rounded-full bg-neutral-200 dark:bg-neutral-900/30">
        <div className="h-full w-full animate-pulse bg-neutral-900 dark:bg-neutral-200" />
      </div>
    </div>
  );

  const renderCompletionState = () => {
    const paymentMethodLabel =
      paymentMethod === "creditCard"
        ? t("Credit Card")
        : paymentMethod === "bankTransfer"
          ? t("Bank Transfer")
          : paymentMethod === "payoneer"
            ? t("Payoneer")
            : paymentMethod === "paypal"
              ? t("PayPal")
              : paymentMethod === "USDT" || paymentMethod === "crypto"
                ? t("USDT")
                : t("Payment");

    return (
      <div className="py-10 w-full flex flex-col items-center text-center">
        {/* Success Icon */}
        <div className="mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-emerald-50 dark:bg-emerald-900/30 shadow-sm">
          <CheckCircle size={40} className="text-emerald-600 dark:text-emerald-400" />
        </div>

        {/* Success Title */}
        <h3 className="text-base font-semibold text-neutral-900 dark:text-white mb-1">
          {t("Payment Successful")}
        </h3>

        {/* Subtext */}
        <p className="text-xs text-neutral-600 dark:text-neutral-300 mb-5 max-w-xs">
          {t("Your payment of")} {formatCurrency(total)} {t("has been processed successfully.")}
        </p>

        {/* Success Message Card */}
        <div className="mb-6 w-full max-w-sm rounded-xl border border-emerald-200/70 bg-emerald-50/70 dark:bg-emerald-900/20 dark:border-emerald-800 px-4 py-3 shadow-sm text-center">
          <p className="text-xs font-medium text-emerald-800 dark:text-emerald-200">
            {paymentMethodLabel} {t("Receipt Submitted Successfully")}
          </p>
        </div>

        {/* Back Button */}
        <button
          onClick={() => navigate("/credit-accounts")}
          className="inline-flex gap-3 items-center rounded-xl border border-neutral-200/80 dark:border-neutral-800 
                   px-5 py-2.5 text-[13px] font-medium text-neutral-700 dark:text-neutral-100 
                   bg-white dark:bg-neutral-900 hover:bg-neutral-50 dark:hover:bg-neutral-800
                   transition-colors"
        >
          <ChevronLeft size={16} />
          {t("Back To Balance Tracking")}
        </button>
      </div>
    );
  };

  const renderErrorState = () => (
    <div className="py-10 text-left">
      <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
        <X size={24} className="text-red-600 dark:text-red-400" />
      </div>
      <div className="mb-1 text-sm font-medium text-neutral-900 dark:text-white">Payment Failed</div>
      <div className="mb-4 text-xs text-red-600 dark:text-red-400">{error}</div>
      <div className="flex gap-2">
        <button
          onClick={() => setError(null)}
          className="rounded-xl bg-neutral-900 px-4 py-2 text-sm font-medium text-white hover:opacity-90 dark:bg-neutral-100 dark:text-neutral-900"
        >
          Try Again
        </button>
        <button
          onClick={() => navigate("/credit-accounts")}
          className="rounded-xl border border-neutral-300 dark:border-neutral-800 px-4 py-2 text-sm font-medium text-neutral-900 dark:text-neutral-100 hover:bg-neutral-50 dark:hover:bg-neutral-800"
        >
          Cancel
        </button>
      </div>
    </div>
  );

  const content =
    isProcessing
      ? renderProcessingState()
      : isComplete
        ? renderCompletionState()
        : error
          ? renderErrorState()
          // Stripe credit card flow — disabled, not in use
          // : currentStep === 2 && paymentMethod === "creditCard"
          //   ? (
          //     <Elements stripe={stripePromise}>
          //       <StripeCardForm ... />
          //     </Elements>
          //   )
          : [StepAmount, StepMethod, StepDetails][currentStep];

  // topbar height from CSS var (fallback)
  const appTopbar =
    parseInt(
      getComputedStyle(document.documentElement).getPropertyValue("--app-topbar"),
      10
    ) || DEFAULT_TOPBAR;

  return (
    <div className="min-h-screen starry-background w-full text-left bg-neutral-50 dark:bg-neutral-900/50">
      <div ref={contentRef} className="mx-auto w-full px-6">
        {/* Header fixed to content column */}
        <header
          ref={headerRef}
          className=" border-b z-10 p-2 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/75 dark:bg-neutral-950/90 dark:border-neutral-900"
          style={{
            position: "fixed",
            left: `${bounds.left}px`,
            width: `${bounds.width}px`,
            top: `${appTopbar}px`,
          }}
        >
          <div className="h-16 flex max-w-5xl mx-auto items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-neutral-100 dark:bg-neutral-900/30 flex items-center justify-center">
                <CreditCard className="text-neutral-900 dark:text-neutral-100" size={18} />
              </div>
              <div>
                <div className="text-neutral-900 dark:text-white text-lg font-semibold tracking-tight">
                  Add Balance
                </div>
                <div className="text-xs text-neutral-500 dark:text-neutral-400">
                  {currentStep === 0 && "Step 1 of 3 · Amount"}
                  {currentStep === 1 && "Step 2 of 3 · Payment Method"}
                  {currentStep === 2 && "Step 3 of 3 · Payment Details"}
                </div>
              </div>
            </div>
            <button
              onClick={() => navigate("/credit-accounts")}
              className="rounded-xl px-3 py-2 text-xs font-medium  text-neutral-700 hover:bg-neutral-100 dark:text-neutral-200 dark:bg-neutral-900/30 dark:hover:bg-neutral-800 transition"
              type="button"
              aria-label="Close"
            >
              Close
            </button>
          </div>
        </header>

        {/* Body */}
        <main
          className="py-32 mt-10 max-w-5xl mx-auto"
          style={{ paddingTop: `${headerH + 8}px` }}
        >
          <div className="grid gap-6 lg:grid-cols-3">
            {/* Main flow */}
            {isComplete ? (
              <div className="lg:col-span-3">{content}</div>
            ) : (
              <div className="lg:col-span-2">{content}</div>
            )}

            {/* Sticky summary (hidden in processing / complete / error) */}
            {!isProcessing &&
              !isComplete &&
              !error && (
                <div
                  className="space-y-5 lg:sticky lg:self-start"
                  style={{ top: `${headerH + 100}px` }}
                >
                  <Card>
                    <div className="mb-3 text-xs font-medium text-neutral-700 dark:text-neutral-200">
                      Summary
                    </div>
                    <PaymentSummary
                      selectedAccount={selectedAccount}
                      amountValue={amountValue}
                      paymentMethod={paymentMethod}
                      total={total}
                      formatCurrency={formatCurrency}
                      t={t}
                    />
                  </Card>

                  <Card>
                    <div className="mb-2 text-xs font-medium text-neutral-700 dark:text-neutral-200">
                      Security
                    </div>
                    <div className="flex items-start gap-2 text-xs text-neutral-600 dark:text-neutral-300">
                      <Info size={14} className="text-neutral-500 dark:text-neutral-400 mt-0.5" />
                      Payments are secured and subject to review. Keep your receipt for verification.
                    </div>
                  </Card>
                </div>
              )}
          </div>
        </main>

        {/* Footer fixed to content column */}
        {!isProcessing && !isComplete && !error && (
          <footer
            className="z-50 border-t p-2 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/75 dark:bg-neutral-950/90 dark:border-neutral-900"
            style={{
              position: "fixed",
              left: `${bounds.left}px`,
              width: `${bounds.width}px`,
              bottom: "0px",
            }}
          >
            <div className="h-16 max-w-5xl mx-auto flex items-center justify-between">
              {currentStep > 0 ? (
                <button
                  type="button"
                  onClick={handlePrev}
                  className="rounded-xl border border-neutral-300 px-4 py-2 text-sm font-medium text-neutral-900 hover:bg-neutral-50 dark:border-neutral-800 dark:text-neutral-100 dark:hover:bg-neutral-800"
                >
                  Back
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => navigate("/credit-accounts")}
                  className="rounded-xl border border-neutral-300 px-4 py-2 text-sm font-medium text-neutral-900 hover:bg-neutral-50 dark:border-neutral-800 dark:text-neutral-100 dark:hover:bg-neutral-800"
                >
                  Cancel
                </button>
              )}

              {currentStep < 2 && (
                <button
                  type="button"
                  onClick={handleNext}
                  disabled={!stepsValid()}
                  className="rounded-xl bg-neutral-900 px-5 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-50 dark:bg-neutral-100 dark:text-neutral-900"
                >
                  Continue
                </button>
              )}
            </div>
          </footer>
        )}
      </div>
    </div>
  );
}
